/**
 * EmployeeNotesTab
 *
 * Register „Gespräche" in `EmployeeForm`. Streng vertraulich:
 * Mitarbeitende sehen ihre eigenen Notizen nicht (Backend setzt 403).
 *
 * UX:
 *  - Composer: Klick auf „+ Neue Notiz" oeffnet ein Inline-Formular.
 *  - Liste: chronologisch absteigend mit Filter, Volltextsuche und
 *    Bearbeitungs-Funktionen.
 *  - Vertraulichkeits-Indikator + Folgetermin-Badge.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import {
  Plus, Pencil, Trash2, Search, Lock, Bell, MessageSquare,
  Target, AlertTriangle, Heart, Stethoscope, Sparkles, ClipboardList,
} from 'lucide-react';
import { employeesAPI } from '../lib/api';

const NOTE_TYPE_META = {
  annual:     { label: 'Jahresgespräch',       icon: ClipboardList, color: 'text-stone-700 border-stone-300 bg-stone-50' },
  goals:      { label: 'Zielvereinbarung',     icon: Target,        color: 'text-emerald-700 border-emerald-300 bg-emerald-50' },
  warning:    { label: 'Abmahnung/Ermahnung',  icon: AlertTriangle, color: 'text-rose-700 border-rose-300 bg-rose-50' },
  feedback:   { label: 'Feedback / Lob',       icon: Sparkles,      color: 'text-amber-700 border-amber-300 bg-amber-50' },
  return:     { label: 'Rückkehrgespräch',     icon: Stethoscope,   color: 'text-sky-700 border-sky-300 bg-sky-50' },
  onboarding: { label: 'Onboarding / Probezeit', icon: Heart,       color: 'text-purple-700 border-purple-300 bg-purple-50' },
  other:      { label: 'Sonstiges',            icon: MessageSquare, color: 'text-stone-700 border-stone-300 bg-stone-50' },
};

const DEFAULT_NEW = (today) => ({
  note_type: 'feedback',
  title: '',
  content: '',
  note_date: today,
  participants: [],
  is_confidential: false,
  follow_up_date: '',
  follow_up_reminder: false,
});

export default function EmployeeNotesTab({ employeeId, canEdit }) {
  const today = format(new Date(), 'yyyy-MM-dd');
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(DEFAULT_NEW(today));
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [onlyOpenFollowUps, setOnlyOpenFollowUps] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadNotes = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (typeFilter !== 'all') params.note_type = typeFilter;
      const res = await employeesAPI.listNotes(employeeId, params);
      setNotes(res.data || []);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      if (err?.response?.status === 403) {
        toast.error(msg || 'Kein Zugriff auf Gesprächsnotizen');
      } else if (err?.response?.status !== 404) {
        toast.error('Notizen konnten nicht geladen werden');
      }
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [employeeId, search, typeFilter]);

  useEffect(() => { loadNotes(); }, [loadNotes]);

  const filteredNotes = useMemo(() => {
    if (!onlyOpenFollowUps) return notes;
    const t = today;
    return notes.filter((n) => n.follow_up_date && n.follow_up_date >= t);
  }, [notes, onlyOpenFollowUps, today]);

  const startEdit = (n) => {
    setEditingId(n.id);
    setForm({
      note_type: n.note_type,
      title: n.title,
      content: n.content,
      note_date: n.note_date,
      participants: n.participants || [],
      is_confidential: !!n.is_confidential,
      follow_up_date: n.follow_up_date || '',
      follow_up_reminder: !!n.follow_up_reminder,
    });
    setComposerOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) return toast.error('Titel ist erforderlich');
    if (!form.content.trim()) return toast.error('Inhalt darf nicht leer sein');
    setBusy(true);
    try {
      const payload = {
        ...form,
        follow_up_date: form.follow_up_date || null,
      };
      if (editingId) await employeesAPI.updateNote(employeeId, editingId, payload);
      else await employeesAPI.createNote(employeeId, payload);
      toast.success(editingId ? 'Notiz aktualisiert' : 'Notiz angelegt');
      setComposerOpen(false);
      setEditingId(null);
      setForm(DEFAULT_NEW(today));
      loadNotes();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Speichern fehlgeschlagen';
      toast.error(typeof msg === 'string' ? msg : 'Speichern fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (n) => {
    if (!window.confirm(`Notiz „${n.title}" wirklich loeschen?`)) return;
    try {
      await employeesAPI.deleteNote(employeeId, n.id);
      toast.success('Notiz gelöscht');
      loadNotes();
    } catch (err) {
      toast.error('Löschen fehlgeschlagen');
    }
  };

  return (
    <div className="space-y-4" data-testid="employee-notes-tab">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
          <Input
            placeholder="Suchen in Titel & Inhalt..."
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="notes-search"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[180px]" data-testid="notes-type-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Gesprächsarten</SelectItem>
            {Object.entries(NOTE_TYPE_META).map(([k, m]) => (
              <SelectItem key={k} value={k}>{m.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
          <Checkbox
            checked={onlyOpenFollowUps}
            onCheckedChange={(v) => setOnlyOpenFollowUps(!!v)}
            data-testid="notes-followups-only"
          />
          Nur offene Folgetermine
        </label>
        {canEdit && (
          <Button
            type="button"
            onClick={() => {
              setEditingId(null);
              setForm(DEFAULT_NEW(today));
              setComposerOpen(true);
            }}
            data-testid="notes-new-btn"
          >
            <Plus className="h-4 w-4 mr-1" />Neue Notiz
          </Button>
        )}
      </div>

      {/* Composer */}
      {composerOpen && (
        <div className="rounded-lg border-2 border-primary/50 bg-primary/5 p-4 space-y-3" data-testid="notes-composer">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>Gesprächsart</Label>
              <Select value={form.note_type} onValueChange={(v) => setForm((f) => ({ ...f, note_type: v }))}>
                <SelectTrigger data-testid="composer-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(NOTE_TYPE_META).map(([k, m]) => (
                    <SelectItem key={k} value={k}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Datum des Gesprächs</Label>
              <Input
                type="date"
                value={form.note_date}
                onChange={(e) => setForm((f) => ({ ...f, note_date: e.target.value }))}
                data-testid="composer-date"
              />
            </div>
            <div>
              <Label>Folgetermin (optional)</Label>
              <Input
                type="date"
                value={form.follow_up_date}
                onChange={(e) => setForm((f) => ({ ...f, follow_up_date: e.target.value }))}
                data-testid="composer-followup"
              />
            </div>
          </div>
          <div>
            <Label>Titel <span className="text-rose-500">*</span></Label>
            <Input
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="z.B. Quartalsfeedback Q1 2026"
              data-testid="composer-title"
            />
          </div>
          <div>
            <Label>Inhalt <span className="text-rose-500">*</span></Label>
            <Textarea
              value={form.content}
              onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
              rows={6}
              placeholder="Vereinbarungen, Anmerkungen, ToDo's..."
              data-testid="composer-content"
            />
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={form.is_confidential}
                onCheckedChange={(v) => setForm((f) => ({ ...f, is_confidential: !!v }))}
                data-testid="composer-confidential"
              />
              <Lock className="h-3.5 w-3.5 text-amber-600" /> Vertraulich
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={form.follow_up_reminder}
                onCheckedChange={(v) => setForm((f) => ({ ...f, follow_up_reminder: !!v }))}
                disabled={!form.follow_up_date}
                data-testid="composer-reminder"
              />
              <Bell className="h-3.5 w-3.5 text-primary" /> Erinnerung an Folgetermin
            </label>
            <div className="flex-1" />
            <Button
              type="button"
              variant="outline"
              onClick={() => { setComposerOpen(false); setEditingId(null); setForm(DEFAULT_NEW(today)); }}
              disabled={busy}
            >
              Abbrechen
            </Button>
            <Button type="button" onClick={handleSave} disabled={busy} data-testid="composer-save">
              {busy ? 'Speichere...' : (editingId ? 'Aktualisieren' : 'Speichern')}
            </Button>
          </div>
        </div>
      )}

      {/* Liste */}
      <div className="space-y-3">
        {loading ? (
          <div className="text-center text-stone-400 py-8">Lade Notizen...</div>
        ) : filteredNotes.length === 0 ? (
          <div className="text-center text-stone-400 py-8 border border-dashed border-stone-200 rounded-lg">
            {notes.length === 0 ? 'Noch keine Gesprächsnotizen vorhanden.' : 'Keine Notizen entsprechen den Filtern.'}
          </div>
        ) : (
          filteredNotes.map((n) => {
            const meta = NOTE_TYPE_META[n.note_type] || NOTE_TYPE_META.other;
            const Icon = meta.icon;
            return (
              <div
                key={n.id}
                className={`rounded-lg border ${meta.color} p-4`}
                data-testid={`notes-row-${n.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-md bg-white/70 border border-current/30 flex items-center justify-center shrink-0">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-base text-stone-900">{n.title}</span>
                        <Badge variant="outline" className="font-normal text-xs">{meta.label}</Badge>
                        {n.is_confidential && (
                          <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50">
                            <Lock className="h-3 w-3 mr-1" /> Vertraulich
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-stone-500 mt-0.5">
                        {format(parseISO(n.note_date), 'EEEE, dd.MM.yyyy', { locale: de })}
                        {' · '}
                        verfasst von <span className="font-medium">{n.author_name}</span>
                      </p>
                    </div>
                  </div>
                  {canEdit && (
                    <div className="flex items-center gap-1 shrink-0">
                      <Button type="button" size="icon" variant="ghost" onClick={() => startEdit(n)} title="Bearbeiten" data-testid={`notes-edit-${n.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button type="button" size="icon" variant="ghost" onClick={() => handleDelete(n)} title="Löschen" data-testid={`notes-delete-${n.id}`}>
                        <Trash2 className="h-4 w-4 text-rose-600" />
                      </Button>
                    </div>
                  )}
                </div>
                <div className="mt-2 whitespace-pre-wrap text-sm text-stone-700">
                  {n.content}
                </div>
                {n.follow_up_date && (
                  <div className="mt-3 flex items-center gap-2 text-xs">
                    <Bell className={`h-3.5 w-3.5 ${n.follow_up_reminder ? 'text-primary' : 'text-stone-400'}`} />
                    <span className="font-medium">Folgetermin:</span>
                    <span>{format(parseISO(n.follow_up_date), 'dd.MM.yyyy', { locale: de })}</span>
                    {n.follow_up_reminder && <Badge variant="outline" className="font-normal">Erinnerung aktiv</Badge>}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
